import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { campagneApi, clientiApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, MoreVertical, Eye, Trash2, Megaphone, Calendar, Euro, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CampagnePage = () => {
  const navigate = useNavigate();
  const [campagne, setCampagne] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCampagna, setSelectedCampagna] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [newCampagna, setNewCampagna] = useState({
    cliente_id: '',
    titolo: '',
    valore_campagna: 0,
    data_inizio: '',
    data_fine: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Carica clienti e campagne separatamente per gestire errori indipendentemente
      const [campagneRes, clientiRes] = await Promise.allSettled([
        campagneApi.getAll(), 
        clientiApi.getAll()
      ]);
      
      if (campagneRes.status === 'fulfilled') {
        setCampagne(campagneRes.value.data);
      } else {
        console.error('Errore caricamento campagne:', campagneRes.reason);
        setCampagne([]);
      }
      
      if (clientiRes.status === 'fulfilled') {
        setClienti(clientiRes.value.data);
      } else {
        console.error('Errore caricamento clienti:', clientiRes.reason);
        toast.error('Errore nel caricamento clienti');
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectCliente = (clienteId) => {
    setNewCampagna({ ...newCampagna, cliente_id: clienteId });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCampagna.cliente_id) { toast.error('Seleziona un cliente'); return; }
    if (!newCampagna.data_inizio || !newCampagna.data_fine) { toast.error('Inserisci le date della campagna'); return; }
    setCreateLoading(true);
    try {
      const response = await campagneApi.create(newCampagna);
      toast.success('Campagna creata con successo');
      setShowCreateDialog(false);
      setNewCampagna({ cliente_id: '', titolo: '', valore_campagna: 0, data_inizio: '', data_fine: '' });
      navigate(`/campagne/${response.data.id}`);
    } catch (error) {
      toast.error('Errore nella creazione');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCampagna) return;
    setDeleteLoading(true);
    try {
      await campagneApi.delete(selectedCampagna.id);
      toast.success('Campagna eliminata (inventario ripristinato)');
      setShowDeleteDialog(false);
      setSelectedCampagna(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatoBadge = (stato) => {
    const variants = { bozza: 'bg-slate-100 text-slate-700', confermata: 'bg-green-100 text-green-700', completata: 'bg-blue-100 text-blue-700' };
    const labels = { bozza: 'Bozza', confermata: 'Confermata', completata: 'Completata' };
    return <Badge className={variants[stato] || variants.bozza}>{labels[stato] || stato}</Badge>;
  };

  const formatCurrency = (value) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

  return (
    <div className="p-6 md:p-10" data-testid="campagne-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight">Campagne</h1>
          <p className="text-slate-500 mt-1">Gestisci le campagne pubblicitarie</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-campagna-btn" className="bg-primary hover:bg-primary/90">
          <Plus size={18} className="mr-2" />Costruisci Campagna
        </Button>
      </div>

      <Card data-testid="campagne-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Megaphone size={20} className="text-primary" />Lista Campagne ({campagne.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : campagne.length === 0 ? (
            <div className="text-center py-12 empty-state rounded-lg">
              <Megaphone size={48} className="mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna campagna trovata</h3>
              <p className="text-slate-500 mb-4">Inizia costruendo la prima campagna pubblicitaria</p>
              <Button onClick={() => setShowCreateDialog(true)}><Plus size={18} className="mr-2" />Costruisci Campagna</Button>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Numero</TableHead>
                    <TableHead className="font-semibold">Codice Cliente</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Titolo</TableHead>
                    <TableHead className="font-semibold">Periodo</TableHead>
                    <TableHead className="font-semibold text-right">Valore</TableHead>
                    <TableHead className="font-semibold text-right">Margine</TableHead>
                    <TableHead className="font-semibold">Stato</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campagne.map((campagna) => (
                    <TableRow key={campagna.id} className="table-row-animate cursor-pointer" onClick={() => navigate(`/campagne/${campagna.id}`)} data-testid={`campagna-row-${campagna.id}`}>
                      <TableCell className="font-mono text-sm">{campagna.numero_campagna}</TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{campagna.cliente_codice}</Badge></TableCell>
                      <TableCell className="font-medium">{campagna.cliente_denominazione}</TableCell>
                      <TableCell>{campagna.titolo || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {campagna.data_inizio && campagna.data_fine ? (
                          <>{format(new Date(campagna.data_inizio), 'dd/MM/yy')} - {format(new Date(campagna.data_fine), 'dd/MM/yy')}<span className="text-slate-400 ml-1">({campagna.numero_giorni}gg)</span></>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(campagna.valore_campagna)}</TableCell>
                      <TableCell className={`text-right font-semibold ${campagna.margine >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(campagna.margine)}</TableCell>
                      <TableCell>{getStatoBadge(campagna.stato)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical size={16} /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/campagne/${campagna.id}`)}><Eye className="mr-2 h-4 w-4" />Modifica</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => { setSelectedCampagna(campagna); setShowDeleteDialog(true); }}><Trash2 className="mr-2 h-4 w-4" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) { setNewCampagna({ cliente_id: '', titolo: '', valore_campagna: 0, data_inizio: '', data_fine: '' }); } }}>
        <DialogContent className="max-w-lg" data-testid="create-campagna-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Costruisci Campagna</DialogTitle>
            <DialogDescription>Seleziona il cliente e imposta i dettagli della campagna</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={newCampagna.cliente_id} onValueChange={handleSelectCliente}>
                  <SelectTrigger data-testid="select-cliente">
                    <SelectValue placeholder="Seleziona un cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clienti.length === 0 ? (
                      <SelectItem value="no-clienti" disabled>Nessun cliente in anagrafica</SelectItem>
                    ) : (
                      clienti.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`cliente-option-${c.id}`}>
                          <span className="font-mono text-xs mr-2">{c.codice}</span> {c.denominazione_sociale}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {clienti.length === 0 && (
                  <p className="text-sm text-amber-600">Nessun cliente disponibile. Vai in Clienti per crearne uno.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="titolo">Titolo Campagna</Label>
                <Input id="titolo" value={newCampagna.titolo} onChange={(e) => setNewCampagna({ ...newCampagna, titolo: e.target.value })} placeholder="Es: Promo Estate 2024" data-testid="input-titolo" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valore"><Euro size={14} className="inline mr-1" />Valore Campagna (netto IVA)</Label>
                <Input id="valore" type="number" min="0" step="0.01" value={newCampagna.valore_campagna} onChange={(e) => setNewCampagna({ ...newCampagna, valore_campagna: parseFloat(e.target.value) || 0 })} data-testid="input-valore" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_inizio"><Calendar size={14} className="inline mr-1" />Data Inizio</Label>
                  <Input id="data_inizio" type="date" value={newCampagna.data_inizio} onChange={(e) => setNewCampagna({ ...newCampagna, data_inizio: e.target.value })} data-testid="input-data-inizio" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_fine"><Calendar size={14} className="inline mr-1" />Data Fine</Label>
                  <Input id="data_fine" type="date" value={newCampagna.data_fine} onChange={(e) => setNewCampagna({ ...newCampagna, data_fine: e.target.value })} data-testid="input-data-fine" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={createLoading} data-testid="submit-create-campagna">{createLoading ? 'Creazione...' : 'Crea Campagna'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600">Elimina Campagna</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la campagna <strong>{selectedCampagna?.numero_campagna}</strong>?
              <br /><br /><span className="text-amber-600">L'inventario degli spot verrà ripristinato automaticamente.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>{deleteLoading ? 'Eliminazione...' : 'Elimina'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampagnePage;
